import classNames from "classnames/bind";
import React, { ReactNode } from "react";
import { Titled } from "react-titled";
import { TabsNavBar } from "../../Tabs";
import { generateDocumentTitle } from "../../utils/generateDocumentTitle";
import type { HeaderProps } from "../types";
import styles from "./Header.module.scss";

const cx = classNames.bind(styles);

const MetaSegment = ({ children }: { children: ReactNode }) => {
  return <div className={cx("meta-segment")}>{children}</div>;
};

const MetaSeparator = () => (
  <div
    className={cx("meta-segment", "meta-separator")}
    role="presentation"
  >
    •
  </div>
);

const MetaSegments = ({ values }: { values: ReactNode[] }) => {
  return (
    <>
      {values
        .filter((v) => !!v)
        .map((v, idx, arr) => (
          <>
            <MetaSegment key={idx}>{v}</MetaSegment>
            {idx < arr.length - 1 ? <MetaSeparator /> : null}
          </>
        ))}
    </>
  );
};

const Header = ({
  actionRow,
  title,
  documentTitleAppName,
  setDocumentTitle,
  category,
  breadcrumbs,
  renderMeta,
  tabs,
  activeTabKey,
  onActiveTabKeyChange,
  isLoading,
}: HeaderProps) => {
  const onTabClick = (newTab: string) => {
    if (onActiveTabKeyChange) {
      onActiveTabKeyChange(newTab);
    }
  };

  return (
    <header className={cx("container")}>
      <div className={cx("contentWrapper")}>
        <hgroup className={cx("header-group")}>
          {category ? (
            <p
              className={cx("category", {
                skeleton: isLoading,
                categorySkeleton: isLoading,
              })}
            >
              {category}
            </p>
          ) : null}
          {setDocumentTitle && typeof title === "string" ? (
            <Titled
              title={generateDocumentTitle(
                title?.replaceAll("undefined", "") || "",
                breadcrumbs?.at(-2)?.title || breadcrumbs?.at(-1)?.title || "",
                {
                  fallbackExistingTitle: documentTitleAppName,
                }
              )}
            >
              <h1 className={cx("title", { skeleton: isLoading })}>{title}</h1>
            </Titled>
          ) : (
            <h1 className={cx("title", { skeleton: isLoading })}>{title}</h1>
          )}
          {renderMeta ? (
            <div className={cx("meta")}>
              {renderMeta({ MetaSegments, MetaSegment, MetaSeparator })}
            </div>
          ) : null}
        </hgroup>

        <div className={cx("actionsContainer")}>{actionRow}</div>
      </div>

      <div className={cx("tabsContainer")}>
        {tabs?.length && activeTabKey ? (
          <TabsNavBar
            onClick={onTabClick}
            tabs={tabs}
            activeKey={activeTabKey}
          />
        ) : null}
      </div>
    </header>
  );
};

export default Header;
